'use client';

import { useState, useMemo } from 'react';
import type { Collection } from '@/lib/shopify/types';
import ProductCard from './product-card';
import CollectionFilters from './collection-filters';
import MobileFilterDrawer from './mobile-filter-drawer';

type SortOption = 'featured' | 'price-asc' | 'price-desc' | 'title-asc' | 'title-desc' | 'newest';

interface CollectionContentProps {
  collection: Collection;
  allCollections: Collection[];
}

export default function CollectionContent({ collection, allCollections }: CollectionContentProps) {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [sortBy, setSortBy] = useState<SortOption>('featured');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const products = useMemo(() => collection.products || [], [collection.products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Apply filters
    Object.entries(selectedFilters).forEach(([filterId, values]) => {
      if (values.length === 0) return;

      result = result.filter((product) => {
        if (filterId === 'productType') {
          return values.includes(product.productType);
        }
        if (filterId === 'vendor') {
          return values.includes(product.vendor);
        }
        // For option-based filters (size, color)
        const option = product.options.find(
          (opt) =>
            opt.name.toLowerCase() === filterId ||
            (filterId === 'color' && opt.name.toLowerCase() === 'colour')
        );
        if (option) {
          return values.some((v) => option.values.includes(v));
        }
        return true;
      });
    });

    // Sort products
    switch (sortBy) {
      case 'price-asc':
        result.sort(
          (a, b) =>
            parseFloat(a.priceRange.minVariantPrice.amount) -
            parseFloat(b.priceRange.minVariantPrice.amount)
        );
        break;
      case 'price-desc':
        result.sort(
          (a, b) =>
            parseFloat(b.priceRange.minVariantPrice.amount) -
            parseFloat(a.priceRange.minVariantPrice.amount)
        );
        break;
      case 'title-asc':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        result.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'newest':
        // Keep original order (assuming newest first from Shopify)
        break;
      default:
        // featured - keep original order
        break;
    }

    return result;
  }, [products, selectedFilters, sortBy]);

  const handleFilterChange = (filterId: string, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[filterId] || [];
      const isSelected = current.includes(value);

      return {
        ...prev,
        [filterId]: isSelected
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
  };

  // Category tabs from collections
  const categoryTabs = useMemo(() => {
    const tabs = [{ id: 'all', name: 'ALL', handle: collection.handle }];

    // Add other collections as tabs (limit to 10)
    allCollections
      .filter((c) => c.handle !== collection.handle)
      .slice(0, 9)
      .forEach((c) => {
        tabs.push({
          id: c.handle,
          name: c.title.toUpperCase(),
          handle: c.handle,
        });
      });

    return tabs;
  }, [allCollections, collection.handle]);

  return (
    <div className="flex gap-8">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-[240px] flex-shrink-0">
        <div className="sticky top-4">
          <CollectionFilters
            products={products}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            totalProducts={products.length}
            filteredCount={filteredProducts.length}
          />
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold uppercase tracking-tight">
            {collection.title}
          </h1>

          {/* Sort Dropdown */}
          <div className="flex items-center gap-4">
            {/* Mobile Filter Button */}
            <button
              onClick={() => setIsMobileFilterOpen(true)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 border border-[var(--color-border)] text-sm font-medium"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                />
              </svg>
              Filters
            </button>

            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="appearance-none bg-white border border-[var(--color-border)] px-4 py-2 pr-10 text-sm font-medium cursor-pointer focus:outline-none focus:border-black"
              >
                <option value="featured">Sort</option>
                <option value="newest">Newest</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="title-asc">Name: A-Z</option>
                <option value="title-desc">Name: Z-A</option>
              </select>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-max pb-2">
            {categoryTabs.map((tab) => (
              <a
                key={tab.id}
                href={tab.id === 'all' ? '#' : `/collections/${tab.handle}`}
                onClick={(e) => {
                  if (tab.id === 'all') {
                    e.preventDefault();
                    setActiveTab('all');
                  }
                }}
                className={`px-4 py-2 text-sm font-medium border transition-colors whitespace-nowrap ${
                  activeTab === tab.id || (tab.id === 'all' && activeTab === 'all')
                    ? 'bg-brand-blue text-white border-brand-blue'
                    : 'bg-white text-black border-[var(--color-border)] hover:border-black'
                }`}
              >
                {tab.name}
              </a>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-lg text-gray-500">No products found</p>
            {Object.keys(selectedFilters).length > 0 && (
              <button
                onClick={handleClearFilters}
                className="mt-4 text-sm underline hover:no-underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Product Count */}
        <div className="mt-8 text-center text-sm text-gray-500">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        products={products}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        totalProducts={products.length}
        filteredCount={filteredProducts.length}
      />
    </div>
  );
}
